/**
 * Corporate accounts: companies, their discount, and when the contract runs.
 *
 * The import is the part that matters. A restaurant has these in a spreadsheet,
 * not in its head, and forty companies typed into a form is how a feature goes
 * unused. So: paste or drop a CSV, see exactly what will happen, then apply.
 *
 * The preview is not a courtesy. The server refuses to write anything while any
 * row is wrong, and this screen shows which rows and why — by the line number
 * the spreadsheet itself displays, so someone can go and fix it.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../lib/api.js';

interface Account {
  id: string;
  name: string;
  name_ar: string | null;
  discount_pct: string;
  contract_from: string | null;
  contract_to: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  is_active: boolean;
  is_current: boolean;
  bookings: number;
}

interface ImportResult {
  total: number;
  valid: number;
  to_create: number;
  to_update: number;
  errors: { line: number; problem: string }[];
  applied: boolean;
}

export function CorporateScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['corporate'],
    queryFn: () => api.get<{ accounts: Account[] }>('/api/v1/corporate-accounts'),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['corporate'] });

  const runImport = useMutation({
    mutationFn: (dryRun: boolean) =>
      api.post<ImportResult>('/api/v1/corporate-accounts/import', { csv, dry_run: dryRun }),
    onSuccess: (res) => {
      setPreview(res);
      setError(null);
      if (res.applied) {
        setCsv('');
        void invalidate();
      }
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : t('errors.generic')),
  });

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/api/v1/corporate-accounts', body),
    onSuccess: () => {
      setShowAdd(false);
      setError(null);
      void invalidate();
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : t('errors.generic')),
  });

  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api.patch(`/api/v1/corporate-accounts/${id}`, body),
    onSuccess: () => void invalidate(),
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : t('errors.generic')),
  });

  if (isLoading) return <p className="text-sm text-slate-500">{t('app.loading')}</p>;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold">{t('corporate.title')}</h1>
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          className="ms-auto rounded-md bg-brand-teal px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-teal-dark"
        >
          {t('corporate.add')}
        </button>
      </div>

      {error ? (
        <p role="alert" className="rounded-md bg-red-50 p-3 text-sm text-red-700">
          {error}
        </p>
      ) : null}

      {showAdd ? (
        <AddAccountForm onCancel={() => setShowAdd(false)} onSubmit={(b) => create.mutate(b)} />
      ) : null}

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="font-semibold">{t('corporate.import')}</h2>
        <p className="mt-1 text-sm text-slate-600">{t('corporate.importHelp')}</p>
        <code className="mt-2 block overflow-x-auto rounded bg-slate-50 px-3 py-2 text-xs text-slate-700">
          name,name_ar,discount_pct,contract_from,contract_to,contact_name,contact_email,contact_phone,notes
        </code>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setCsv(await file.text());
              setPreview(null);
            }}
            className="text-sm"
          />
        </div>

        <textarea
          value={csv}
          onChange={(e) => {
            setCsv(e.target.value);
            setPreview(null);
          }}
          rows={5}
          placeholder={t('corporate.pastePlaceholder')}
          className="mt-3 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs"
        />

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={!csv.trim() || runImport.isPending}
            onClick={() => runImport.mutate(true)}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {t('corporate.check')}
          </button>
          <button
            type="button"
            // Only after a clean preview. The server would refuse anyway; not
            // offering the button avoids teaching people to click through an
            // error they have not read.
            disabled={!preview || preview.errors.length > 0 || preview.applied || runImport.isPending}
            onClick={() => runImport.mutate(false)}
            className="rounded-md bg-brand-teal px-3 py-2 text-sm font-medium text-white hover:bg-brand-teal-dark disabled:opacity-50"
          >
            {preview
              ? t('corporate.applyCount', { create: preview.to_create, update: preview.to_update })
              : t('corporate.apply')}
          </button>
        </div>

        {preview ? (
          <div className="mt-3 rounded-md border border-slate-200 p-3 text-sm">
            <p className={preview.applied ? 'font-medium text-emerald-700' : 'text-slate-700'}>
              {preview.applied
                ? t('corporate.imported', { create: preview.to_create, update: preview.to_update })
                : t('corporate.previewSummary', {
                    valid: preview.valid,
                    total: preview.total,
                  })}
            </p>
            {preview.errors.length > 0 ? (
              <>
                <p className="mt-2 font-medium text-red-700">{t('corporate.nothingWritten')}</p>
                <ul className="mt-1 space-y-0.5 text-red-700">
                  {preview.errors.map((e) => (
                    <li key={e.line}>
                      {t('corporate.line', { line: e.line })}: {e.problem}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        ) : null}
      </section>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 text-slate-500">
            <tr>
              <th scope="col" className="px-4 py-2 text-start font-medium">{t('corporate.company')}</th>
              <th scope="col" className="px-4 py-2 text-start font-medium">{t('corporate.discount')}</th>
              <th scope="col" className="px-4 py-2 text-start font-medium">{t('corporate.contract')}</th>
              <th scope="col" className="px-4 py-2 text-start font-medium">{t('corporate.bookings')}</th>
              <th scope="col" className="px-4 py-2 text-end font-medium">{t('corporate.status')}</th>
            </tr>
          </thead>
          <tbody>
            {(data?.accounts ?? []).map((a) => (
              <tr key={a.id} className="border-b border-slate-100 last:border-0">
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{a.name}</div>
                  {a.name_ar ? <div className="text-slate-500">{a.name_ar}</div> : null}
                  {a.contact_email ? (
                    <div className="text-xs text-slate-500" dir="ltr">{a.contact_email}</div>
                  ) : null}
                </td>
                <td className="px-4 py-3 font-mono" data-numeric>
                  {Number(a.discount_pct).toFixed(2)}%
                </td>
                <td className="px-4 py-3" data-numeric>
                  {a.contract_from || a.contract_to ? (
                    <span className="font-mono text-xs">
                      {a.contract_from ?? '—'} → {a.contract_to ?? '—'}
                    </span>
                  ) : (
                    <span className="text-slate-400">{t('corporate.noContract')}</span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono" data-numeric>{a.bookings}</td>
                <td className="px-4 py-3 text-end">
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {/* is_current is not is_active: an account can be switched
                        on and still outside its contract dates, which is
                        exactly the case someone needs to see. */}
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        a.is_current
                          ? 'bg-emerald-50 text-emerald-700'
                          : a.is_active
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {a.is_current
                        ? t('corporate.current')
                        : a.is_active
                          ? t('corporate.outOfContract')
                          : t('corporate.disabled')}
                    </span>
                    <button
                      type="button"
                      onClick={() => update.mutate({ id: a.id, is_active: !a.is_active })}
                      className="rounded-md border border-slate-200 px-2 py-1 text-slate-600 hover:bg-slate-50"
                    >
                      {a.is_active ? t('corporate.disable') : t('corporate.enable')}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AddAccountForm({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '',
    name_ar: '',
    discount_pct: '0',
    contract_from: '',
    contract_to: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
  });
  const set = (k: keyof typeof form, v: string) => setForm((c) => ({ ...c, [k]: v }));

  return (
    <form
      className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          name: form.name,
          name_ar: form.name_ar || null,
          discount_pct: Number(form.discount_pct),
          contract_from: form.contract_from || null,
          contract_to: form.contract_to || null,
          contact_name: form.contact_name || null,
          contact_email: form.contact_email || null,
          contact_phone: form.contact_phone || null,
        });
      }}
    >
      <Labelled label={t('corporate.company')}>
        <input required value={form.name} onChange={(e) => set('name', e.target.value)} className={inputClass} />
      </Labelled>
      <Labelled label={t('corporate.companyAr')}>
        <input dir="rtl" value={form.name_ar} onChange={(e) => set('name_ar', e.target.value)} className={inputClass} />
      </Labelled>
      <Labelled label={t('corporate.discount')}>
        <input
          type="number"
          min={0}
          max={100}
          step="0.01"
          value={form.discount_pct}
          onChange={(e) => set('discount_pct', e.target.value)}
          className={`${inputClass} font-mono`}
        />
      </Labelled>
      <Labelled label={t('corporate.contactName')}>
        <input value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} className={inputClass} />
      </Labelled>
      <Labelled label={t('corporate.from')}>
        <input type="date" value={form.contract_from} onChange={(e) => set('contract_from', e.target.value)} className={inputClass} />
      </Labelled>
      <Labelled label={t('corporate.to')}>
        <input type="date" value={form.contract_to} onChange={(e) => set('contract_to', e.target.value)} className={inputClass} />
      </Labelled>
      <Labelled label={t('corporate.contactEmail')}>
        <input dir="ltr" type="email" value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} className={inputClass} />
      </Labelled>
      <Labelled label={t('corporate.contactPhone')}>
        <input dir="ltr" value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} className={inputClass} />
      </Labelled>

      <div className="flex items-end gap-2 sm:col-span-2">
        <button type="submit" className="rounded-md bg-brand-teal px-3 py-2 text-sm font-medium text-white hover:bg-brand-teal-dark">
          {t('corporate.save')}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
          {t('corporate.cancel')}
        </button>
      </div>
    </form>
  );
}

const inputClass = 'w-full rounded-md border border-slate-300 px-3 py-2 text-sm';

function Labelled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-slate-600">{label}</span>
      {children}
    </label>
  );
}
